import React, { FC, useState } from 'react';
import ReactSlider, { ReactSliderProps } from 'react-slider';
import styled from 'styled-components';
import {
  extractStylingProps,
  propStyling,
  PropsWithPropStyling,
  tombac,
  useTombac,
} from '../../shared';
import { Box } from '../Styling/Box';

export type SliderProps = PropsWithPropStyling<
  ReactSliderProps & {
    marks?: SliderMarks;
  }
>;

export const Slider: FC<SliderProps> = ({
  defaultValue = 0,
  marks,
  max = 100,
  min = 0,
  onChange,
  value: valueProp,
  ...rest
}) => {
  const [stylingProps, reactSliderProps] = extractStylingProps(rest);

  const sliderMarks = areMarksComplex(marks)
    ? marks.map((m) => m.value)
    : marks;

  const [value, setValue] = useState<number>(() => {
    let initialValue = valueProp ?? defaultValue;
    if (initialValue < min) {
      initialValue = min;
    } else if (initialValue > max) {
      initialValue = max;
    }
    return initialValue;
  });

  const handleChange = (value: number, index: number) => {
    setValue(value);
    onChange?.(value, index);
  };

  return (
    <Box
      $boxSizing="content-box"
      $display="flex"
      $flexDirection="column"
      $minHeight="10u"
      $pt="7u"
      className="TombacSlider"
      {...stylingProps}
    >
      <ReactSlider
        defaultValue={defaultValue}
        marks={sliderMarks}
        max={max}
        min={min}
        onChange={handleChange}
        renderThumb={SliderParts.Thumb}
        renderTrack={SliderParts.Track}
        value={valueProp}
        {...reactSliderProps}
      />
      {areMarksComplex(marks) ? (
        <SliderMarkLabels marks={marks} max={max} min={min} value={valueProp ?? value} />
      ) : null}
    </Box>
  );
};

export type SliderComplexMark = { label: string; value: number };

export type SliderMarks = ReactSliderProps['marks'] | SliderComplexMark[];

export const SliderParts = {
  Thumb: (((props, state) => (
    <SliderParts.StyledThumb {...(props as any)} />
  )) as ReactSliderProps['renderThumb']) as any,

  Track: (((props, state) => (
    <SliderParts.StyledTrack {...(props as any)} index={state.index} />
  )) as ReactSliderProps['renderTrack']) as any,

  StyledThumb: styled.div`
    background-color: ${tombac.color('primary')};
    border-radius: 50%;
    height: ${tombac.unit(18)};
    margin-top: ${tombac.unit(-8)};
    transition: 0.1s ease-out;
    transition-property: background-color, box-shadow;
    width: ${tombac.unit(18)};

    &:focus,
    &:focus-visible {
      box-shadow: 0 0 0 ${tombac.unit(3)} ${tombac.color('danger', 200)};
      outline: none;
    }
    &:focus:not(:focus-visible) {
      box-shadow: initial;
    }

    &:active,
    &:hover {
      background-color: ${tombac.color('primary', 600)};
    }
  `,

  StyledTrack: styled.div<{ index: number }>`
    background-color: ${({ index }) =>
      index >= 1 ? tombac.color('neutral') : tombac.color('primary')};
    height: ${tombac.unit(3)};
  `,
} as const;

const SliderMarkLabels: FC<
  Required<Pick<SliderProps, 'max' | 'min'>> &
    Pick<SliderProps, 'value'> & {
      marks: SliderComplexMark[];
    }
> = ({ marks, max, min, value }) => {
  const tombac = useTombac();

  return (
    <Box $h="18u" $mt="11u" $position="relative">
      {marks.map((mark) => (
        <Box
          $left={`${((mark.value - min) / (max - min)) * 100}%`}
          $position="absolute"
          key={mark.value}
        >
          <MarkLabelText
            $ml={`-${((mark.value - min) / (max - min)) * 100}%`}
            active={value === mark.value}
          >
            {mark.label}
          </MarkLabelText>
        </Box>
      ))}
    </Box>
  );
};

function areMarksComplex(marks: SliderMarks): marks is SliderComplexMark[] {
    return Boolean(
      marks &&
        Array.isArray(marks) &&
        marks.length > 0 &&
        typeof marks[0] !== 'number',
    );
  }

const MarkLabelText = styled.span<PropsWithPropStyling<{ active: boolean }>>`
  color: ${({ active }) => tombac.color('neutral', active ? 900 : 600)};
  display: block;
  font-family: ${tombac.fontFamily};
  font-size: ${tombac.unit(12)};
  font-weight: ${({ active }) => (active ? '700' : '400')};
  transition: 0.2s ease-out;
  transition-property: color, font-weight;
  ${propStyling}
`;
